import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { api } from '../lib/api';
import { ErrorNotice } from '../ui/feedback';
import { DataTable, PageHead } from '../ui/table';
import { DefList, ItemId, Mono, Num, Position, Time } from '../ui/values';
import { BackLink, LocalPager, useLocalPaged, VariantLabel } from './common';
import { paths } from './paths';
import type { AdminItemDetail, ItemLocation } from './types';

/**
 * Item (design.md §6): „Katalog“ (was es gibt) und „Bestand in Kisten“ (wie viel wo liegt) sind
 * sichtbar getrennte Abschnitte mit eigenen Überschriften.
 */
export function ItemDetailPage() {
  const { id = '' } = useParams();
  const item = useQuery({ queryKey: ['admin', 'item', id], queryFn: () => api.get<AdminItemDetail>(`/admin/items/${encodeURIComponent(id)}`) });
  const places = useLocalPaged<ItemLocation>(['admin', 'item-chests', id], `/admin/items/${encodeURIComponent(id)}/chests`, {});

  if (item.isError) return <ErrorNotice error={item.error} onRetry={() => item.refetch()} />;
  const d = item.data;
  if (!d) return <p className="muted">Lädt…</p>;
  const total = d.variants.reduce((n, v) => n + v.total, 0);
  // Nur der Name des Basis-Items; reine Varianten-Items (z. B. verzauberte Bücher) haben keinen eigenen
  const name = d.variants.find((v) => !v.components)?.name ?? null;

  return (
    <div className="stack-lg">
      <PageHead back={<BackLink to={paths.items} label="Items" />} title={<ItemId id={d.id} />} context={name ?? undefined} />

      <section className="stack">
        <h2>Katalog</h2>
        <DefList
          items={[
            ['Item-ID', <Mono value={d.id} />],
            ['Anzeigename', name ?? <span className="muted">{d.variants.length ? 'nur Varianten' : 'nicht gemeldet'}</span>],
            ['Varianten', <Num value={d.variants.length} />],
            ['Gesamtmenge', <Num value={total} />],
            ['Zuerst gesehen', <Time iso={d.firstSeenAt} absolute />],
          ]}
        />
        <DataTable
          label="Varianten"
          rows={d.variants}
          rowKey={(v) => v.id}
          empty="Keine Varianten."
          columns={[
            { key: 'v', header: 'Variante', cell: (v) => <VariantLabel variant={v} /> },
            { key: 'chests', header: 'in Kisten', num: true, cell: (v) => <Num value={v.chestCount} /> },
            { key: 'total', header: 'Menge', num: true, cell: (v) => <Num value={v.total} /> },
            { key: 'seen', header: 'zuerst gesehen', hide: 'sm', cell: (v) => <Time iso={v.firstSeenAt} /> },
          ]}
        />
      </section>

      <section className="stack">
        <h2>Bestand in Kisten</h2>
        {places.q.isError && <ErrorNotice error={places.q.error} onRetry={() => places.q.refetch()} />}
        <DataTable
          label="Bestand in Kisten"
          rows={places.q.data?.items}
          loading={places.q.isPending}
          rowKey={(p) => p.chestId}
          rowHref={(p) => paths.chest(p.chestId)}
          empty="Liegt derzeit in keiner Kiste."
          columns={[
            { key: 'name', header: 'Kiste', cell: (p) => p.name ?? <span className="muted">ohne Namen</span> },
            { key: 'owner', header: 'Besitzer', cell: (p) => p.owner.name ?? <span className="muted">–</span> },
            { key: 'server', header: 'Server', hide: 'sm', cell: (p) => <span className="mono">{p.server}</span> },
            { key: 'pos', header: 'Position', cell: (p) => <Position dimension={p.dimension} pos={p.pos} /> },
            { key: 'total', header: 'Menge', num: true, cell: (p) => <Num value={p.total} /> },
          ]}
        />
        <LocalPager paged={places} />
      </section>
    </div>
  );
}
